import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('kyber_token'));
    const [loading, setLoading] = useState(true);
    const logoutRef = useRef(null);

    const api = useMemo(() => {
        const instance = axios.create({
            baseURL: `${API_URL}/api`,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        instance.interceptors.request.use((config) => {
            const storedToken = localStorage.getItem('kyber_token');
            if (storedToken) {
                config.headers.Authorization = `Bearer ${storedToken}`;
            }
            return config;
        });

        instance.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    if (logoutRef.current) logoutRef.current();
                }
                return Promise.reject(error);
            }
        );

        return instance;
    }, []);

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            localStorage.removeItem('kyber_token');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token, api]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password) => {
        const response = await api.post('/auth/login', { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('kyber_token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const register = async (email, password, name) => {
        const response = await api.post('/auth/register', { email, password, name });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('kyber_token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const logout = useCallback(() => {
        localStorage.removeItem('kyber_token');
        setToken(null);
        setUser(null);
    }, []);

    logoutRef.current = logout;

    const updateUser = async (data) => {
        const response = await api.put('/auth/me', data);
        setUser(response.data);
        return response.data;
    };

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        updateUser,
        api,
        isAdmin: user?.role === 'admin',
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
